import {Dialog, TextField} from "@mui/material";
import {defineMessage, FormattedMessage, MessageDescriptor} from "react-intl";
import {useEffect, useMemo, useState} from "react";
import {FieldError, Form, FormHandler, NullHandler} from "./Form";
import {
    DialogActions,
    DialogContent,
    FormattedDialogTitle,
    FormattedFormRow,
    FormContainer,
    FormDialogActionButtons,
} from "@dvrt/graphql-widgets";
import {KeysMatching} from "@/types";
import {AddressBookEntryData} from "@/api";

type Props = {
    target: string;
    close: () => void;
    onSuccess: () => void;
    handler: FormHandler;
};

const FormattedError = (props: {error: FieldError}) => {
    // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
    return props.error ? <FormattedMessage id={props.error.type} values={props.error.context} /> : null;
};

const parseArrayFieldValue = (value: string): string[] =>
    value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

const areStringArraysEqual = (first: string[], second: string[]): boolean =>
    first.length === second.length && first.every((value, index) => value === second[index]);

const Field = ({
    name,
    label,
    form,
    required,
}: {
    name: KeysMatching<AddressBookEntryData, string>;
    label: MessageDescriptor;
    form: Form;
    required?: boolean;
}) => (
    <FormattedFormRow label={label} required={required}>
        <TextField
            variant="standard"
            fullWidth
            margin="dense"
            type="text"
            value={form.data[name]}
            error={form.hasError(name)}
            helperText={<FormattedError error={form.errors[name]} />}
            onChange={form.createDataUpdater(name)}
        />
    </FormattedFormRow>
);

const ArrayField = ({
    name,
    label,
    form,
    required,
}: {
    name: "telephoneNumber";
    label: MessageDescriptor;
    form: Form;
    required?: boolean;
}) => {
    const data = useMemo(() => form.data[name] ?? [], [form.data, name]);
    const [displayValue, setDisplayValue] = useState(data.join(", "));

    useEffect(() => {
        if (!areStringArraysEqual(parseArrayFieldValue(displayValue), data)) setDisplayValue(data.join(", "));
    }, [data, displayValue]);

    const handleChange = (value: string) => {
        setDisplayValue(value);

        const parsedValue = parseArrayFieldValue(value);
        if (!areStringArraysEqual(parsedValue, data)) form.setData({...form.data, [name]: parsedValue});
    };

    return (
        <FormattedFormRow label={label} required={required}>
            <TextField
                variant="standard"
                fullWidth
                margin="dense"
                type="text"
                value={displayValue}
                error={form.hasError(name)}
                helperText={form.errors[name] && <FormattedError error={form.errors[name]} />}
                onChange={(ev) => handleChange(ev.target.value)}
            />
        </FormattedFormRow>
    );
};

export default (props: Props) => {
    const {target, onSuccess} = props;

    const show = !!props.handler;

    const handler = props.handler || new NullHandler();

    const form = new Form(target, handler);

    const close = () => {
        form.reset();
        props.close();
    };

    async function submit() {
        const success = await form.submit();
        if (!success) return;
        form.reset();
        onSuccess();
        close();
    }

    return (
        <Dialog open={show}>
            <FormattedDialogTitle title={handler.title} />
            <DialogContent>
                <FormContainer style={{maxWidth: 400}}>
                    <Field
                        form={form}
                        name="sn"
                        label={defineMessage({id: "address_book.sn", defaultMessage: "Surname"})}
                        required
                    />
                    <Field
                        form={form}
                        name="givenName"
                        label={defineMessage({id: "address_book.givenName", defaultMessage: "Given name"})}
                        required
                    />
                    <ArrayField
                        form={form}
                        name="telephoneNumber"
                        label={defineMessage({id: "address_book.telephoneNumber", defaultMessage: "Telephone number"})}
                        required
                    />
                    <Field
                        form={form}
                        name="mail"
                        label={defineMessage({id: "address_book.mail", defaultMessage: "Email"})}
                    />
                    <Field
                        form={form}
                        name="o"
                        label={defineMessage({id: "address_book.o", defaultMessage: "Company"})}
                    />
                    <Field
                        form={form}
                        name="mobile"
                        label={defineMessage({id: "address_book.mobile", defaultMessage: "Mobile number"})}
                    />
                    <Field
                        form={form}
                        name="homePhone"
                        label={defineMessage({id: "address_book.homePhone", defaultMessage: "Home number"})}
                    />
                    <Field
                        form={form}
                        name="departmentNumber"
                        label={defineMessage({
                            id: "address_book.departmentNumber",
                            defaultMessage: "Department number",
                        })}
                    />
                    <Field
                        form={form}
                        name="description"
                        label={defineMessage({id: "address_book.description", defaultMessage: "Description"})}
                    />
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <FormDialogActionButtons
                    cancel={close}
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    enabled={form.isChanged}
                />
            </DialogActions>
        </Dialog>
    );
};
